/**
 * collect 화면 (TASK-2026-004 SPEC 「포함 범위」 1~6, AC-01·02·04·05).
 *
 * 참가자가 진행자 없이 혼자 통과하는 경로다. 안내·동의 → 초대코드 → Q1 → 게임 →
 * (조기 종료 포함) 설문 → 서버 확인까지 이 화면이 이끈다.
 *
 * 이 화면이 지키는 것:
 * - **동의 전에는 원격 요청이 없다.** 캠페인 안내(공개 정보) 외에는 세션 생성이 첫 요청이다.
 *   캠페인 질의값이 없으면 온라인 모드가 아니므로 아무 API도 부르지 않는다(OF1-02).
 * - **저장 상태를 사실대로 말한다.** `전송 중 / 일부 미전송 / 저장 완료`를 구별하고,
 *   완료는 서버가 마지막 기록과 최종 응답을 확인한 뒤에만 표시한다(F1-04.8).
 * - **끝까지 못 해도 낼 수 있다.** 설문은 게임을 얼마나 했든 열리고, 모든 문항에
 *   "모르겠다 / 응답하지 않음"이 있다.
 * - **320px·터치·키보드에서 쓸 수 있다.** 조작 영역은 한 열로 흐르고 버튼은 44px 이상이다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { checkpointsFor } from '../app/checkpoint';
import { setRecordingSession, telemetryFor } from '../app/telemetry';
import { createApi } from '../online/api';
import { closeDanglingAttempt } from '../online/dangling';
import { collectUrl, isOnline, playUrl, readEnding, readMode, readStep, type Ending } from '../online/mode';
import { resultFileName, type VerifiedResult } from '../online/result';
import type { ConnectionState } from '../online/connectivity';
import { createPageSender, pageConnectivity, startSenderLoop } from '../online/runtime';
import type { Sender, SenderStatus } from '../online/sender';
import { clearSessionState, readQueueState, type OnlineSessionState } from '../online/state';
import { collectKo as t } from '../strings/collect';
import { BUTTON, PRIMARY, dontKnowButton, primaryButton } from './buttonStyle';
import { finalizeSubmission } from './finalize';
import { doneView, statusLabel } from './savedStatus';
import {
  confirmLockedFinal,
  fetchResult,
  loadConfig,
  recoverPending,
  restore,
  resumePending,
  saveAnswers,
  startSession,
  submit,
  withdraw,
  type FlowDeps,
  type StartOutcome,
} from './flow';
import {
  charCount,
  emptyAnswers,
  isAnswered,
  MAX_FIELD_CHARS,
  questionsOf,
  unansweredIds,
  type AnswerIssue,
  type AnswerValue,
  type Question,
} from './survey';
import type { PublicConfig } from '../online/client';

type Screen =
  | 'loading'
  | 'local'
  | 'closed'
  | 'missing'
  | 'unreachable'
  | 'consent'
  | 'declined'
  /** 링크와 저장된 참여의 캠페인·동의 버전이 다르다. 진행·전송을 막는다(REV-008). */
  | 'mismatch'
  | 'invite'
  /** 세션 생성 결과를 아직 확정하지 못했다. 같은 intent로 다시 확인한다(TASK-2026-011 CR-01 §4). */
  | 'pending'
  | 'q1'
  | 'play'
  | 'survey'
  | 'done';

/** 세션 시작 거부 사유를 참가자 문구로 바꾼다. 공개·초대코드 캠페인이 같은 문구를 쓴다. */
function startRejection(code: string): string {
  switch (code) {
    case 'invite_invalid':
      return t.invite.invalid;
    case 'campaign_full':
      return t.invite.full;
    case 'campaign_daily_limit':
      return t.invite.dailyLimit;
    case 'rate_limited':
      return t.invite.limited;
    case 'consent_version_mismatch':
      return t.consent.versionChanged;
    case 'campaign_closed':
      return t.campaign.closed;
    case 'active_session_exists':
      return t.start.activeElsewhere;
    case 'session_intent_expired':
    case 'session_intent_required':
      return t.start.expired;
    default:
      return t.invite.failed;
  }
}

function recoveryFileName(at: number = Date.now()): string {
  const date = new Date(at);
  const pad = (value: number): string => String(value).padStart(2, '0');
  const stamp = `${String(date.getFullYear())}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `ColorCubeAtlas_GateF1_Collect_Recovery_${stamp}.json`;
}


const CARD: React.CSSProperties = {
  border: '1px solid #D8D8D8',
  borderRadius: '10px',
  padding: '1rem',
  marginBottom: '1rem',
  maxWidth: '34rem',
};

interface FieldProps {
  readonly question: Question;
  readonly value: AnswerValue;
  readonly issue: AnswerIssue | undefined;
  readonly onChange: (value: AnswerValue) => void;
}

/** 문항 한 개. 어떤 문항이든 "모르겠다 / 응답하지 않음"으로 돌아갈 수 있다. */
function Field({ question, value, issue, onChange }: FieldProps) {
  const id = `q-${question.id}`;
  const unanswered = !isAnswered(value);
  const scale = question.scale;
  return (
    <fieldset
      style={{ border: 0, padding: 0, margin: '0 0 1.25rem' }}
      data-testid={`field-${question.id}`}
      data-unanswered={unanswered ? 'true' : 'false'}
    >
      <legend style={{ fontWeight: 600, padding: 0 }}>{question.label}</legend>
      {question.help !== undefined && (
        <p style={{ margin: '0.25rem 0 0.5rem', color: '#4A4A4A', fontSize: '0.9rem' }}>{question.help}</p>
      )}

      {question.kind === 'text' && (
        <>
          <textarea
            id={id}
            aria-label={question.label}
            value={typeof value === 'string' ? value : ''}
            rows={3}
            onChange={(event) => {
              onChange(event.target.value);
            }}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: '1rem', padding: '0.5rem', minHeight: '44px' }}
          />
          <div style={{ fontSize: '0.8rem', color: '#6B6B6B' }}>{t.survey.chars(charCount(value), MAX_FIELD_CHARS)}</div>
        </>
      )}

      {question.kind === 'scale' && scale !== undefined && (
        <div role="radiogroup" aria-label={question.label} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {[1, 2, 3, 4, 5].map((score) => (
            <label key={score} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', minHeight: '44px' }}>
              <input
                type="radio"
                name={id}
                checked={value === score}
                onChange={() => {
                  onChange(score);
                }}
              />
              {score === 1 ? `1 ${scale.minLabel}` : score === 5 ? `5 ${scale.maxLabel}` : String(score)}
            </label>
          ))}
        </div>
      )}

      {question.kind === 'choice' && (
        <div role="radiogroup" aria-label={question.label} style={{ display: 'grid', gap: '0.25rem' }}>
          {(question.options ?? []).map((option) => (
            <label key={option} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', minHeight: '44px' }}>
              <input
                type="radio"
                name={id}
                checked={value === option}
                onChange={() => {
                  onChange(option);
                }}
              />
              {option}
            </label>
          ))}
        </div>
      )}

      {question.kind === 'multi' && (
        <div style={{ display: 'grid', gap: '0.25rem' }}>
          {(question.options ?? []).map((option) => {
            const list = Array.isArray(value) ? value : [];
            return (
              <label key={option} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', minHeight: '44px' }}>
                <input
                  type="checkbox"
                  checked={list.includes(option)}
                  onChange={(event) => {
                    const next = event.target.checked ? [...list, option] : list.filter((item) => item !== option);
                    onChange(next.length === 0 ? null : next);
                  }}
                />
                {option}
              </label>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
        <button
          type="button"
          style={dontKnowButton(unanswered)}
          aria-pressed={unanswered}
          data-testid={`dont-know-${question.id}`}
          onClick={() => {
            onChange(null);
          }}
        >
          {unanswered && <span aria-hidden="true">✓ </span>}
          {t.survey.dontKnow}
        </button>
      </div>

      {issue !== undefined && (
        <p role="alert" style={{ color: '#B00020', margin: '0.5rem 0 0' }}>
          {issue.code === 'too_long'
            ? t.survey.tooLong
            : issue.code === 'contact_like'
              ? t.survey.contactLike
              : t.survey.invalid}
        </p>
      )}
    </fieldset>
  );
}

export function CollectApp({ search }: { readonly search: string }) {
  const mode = useMemo(() => readMode(search), [search]);
  const api = useMemo(() => createApi(), []);
  const [screen, setScreen] = useState<Screen>(isOnline(mode) ? 'loading' : 'local');
  const [config, setConfig] = useState<PublicConfig | undefined>(undefined);
  const [session, setSession] = useState<OnlineSessionState | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>(() => emptyAnswers());
  const [issues, setIssues] = useState<readonly AnswerIssue[]>([]);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [inviteCode, setInviteCode] = useState('');
  const [consented, setConsented] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<SenderStatus | undefined>(undefined);
  const [digest, setDigest] = useState<string | undefined>(undefined);
  const [blocking, setBlocking] = useState<readonly string[]>([]);
  /** 서버가 확정한 단일 결과 envelope. 검증에 성공했을 때만 채운다(REV-007). */
  const [result, setResult] = useState<VerifiedResult | undefined>(undefined);
  /** 어긋난 캠페인·동의 버전 (REV-008). */
  /** `cohort`: 같은 코호트의 다른 모집 링크로 이미 참여했다(TASK-2026-011). */
  const [mismatch, setMismatch] = useState<
    { readonly reason: 'campaign' | 'consent_version' | 'session' | 'cohort'; readonly current: string } | undefined
  >(undefined);

  /**
   * 서버 연결 점검 결과 (TASK-2026-012 CR-01). 브라우저의 온라인 표시가 아니라 실제 점검이다.
   * 연습 화면(캠페인 없음)은 점검을 만들지 않으므로 원격 요청이 없다.
   */
  const link = useMemo(() => (isOnline(mode) ? pageConnectivity() : undefined), [mode]);
  const [connection, setConnection] = useState<ConnectionState>(() => link?.state() ?? 'unknown');
  /** 이 화면에서 최종 답변을 서버에 저장했다. 이후 입력은 서버에 반영되지 않으므로 막는다. */
  const [finalSaved, setFinalSaved] = useState(false);
  /** 캠페인 안내를 다시 읽을 차례. 연결이 돌아오면 늘려 첫 진입을 다시 한다. */
  const [loadRound, setLoadRound] = useState(0);
  /** 잠긴 최종 응답이 이 화면의 답과 같다고 확인되지 않아 제출을 멈췄다 (REV-003). */
  const [finalConflict, setFinalConflict] = useState(false);

  /**
   * 서버 요청 실패를 연결 점검에 알린다 (TASK-2026-012 CR-01 REV-002). **통신 오류만** 넘긴다.
   * 서버가 답한 거절(4xx·세션 거부·요청 한도)은 연결 문제가 아니므로 연결 상태를 바꾸지 않는다.
   */
  const noteFailure = useCallback(
    (code: string) => {
      if (code === 'network_error') {
        link?.reportFailure();
      }
    },
    [link],
  );

  const senderRef = useRef<Sender | undefined>(undefined);
  const ending: Ending = readEnding(search) ?? 'early_exit';
  const requestedSurvey = readStep(search) === 'survey';

  const deps: FlowDeps = useMemo(
    () => ({ api, storage: window.localStorage, now: () => Date.now() }),
    [api],
  );

  /** 세션이 생기면 전송기를 만든다. 세션이 없으면 아무 전송도 없다. */
  const ensureSender = useCallback(
    (active: OnlineSessionState): Sender => {
      const existing = senderRef.current;
      if (existing !== undefined) {
        return existing;
      }
      /*
       * 이 뒤로 이 페이지의 기록은 세션 키 공간에 쓴다 (R2-REV-001). 동의 전·연습·다른
       * 탭의 기록은 원래 자리에 남고 이 세션으로 섞이지 않는다.
       */
      setRecordingSession(active.session_ref);
      const sender = createPageSender(api, active);
      senderRef.current = sender;
      setStatus(sender.status());
      return sender;
    },
    [api],
  );

  // ── 첫 진입: 캠페인 안내와 세션 복구 ────────────────────────────────────
  useEffect(() => {
    if (!isOnline(mode)) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const outcome = await loadConfig(deps, mode.campaignId);
      if (cancelled) {
        return;
      }
      if (outcome.kind === 'not_found') {
        setScreen('missing');
        return;
      }
      if (outcome.kind === 'unreachable') {
        // 통신 오류면 연결 점검에 알린다. 점검 버튼·예약 재확인이 이 화면에도 붙는다(REV-001).
        noteFailure(outcome.code);
        setScreen('unreachable');
        setNotice(t.campaign.unreachable);
        return;
      }
      setConfig(outcome.config);

      const restored = await restore(deps, {
        campaignId: mode.campaignId,
        consentVersion: outcome.config.consent_version,
      });
      if (cancelled) {
        return;
      }
      if (restored.kind === 'mismatch') {
        /*
         * 링크와 저장된 참여가 다르다 (REV-008). 전송기를 만들지 않으므로 이 화면에서는
         * 어느 쪽으로도 쓰지 않는다. 참가자가 고르기 전까지 진행을 멈춘다.
         */
        setMismatch({ reason: restored.reason, current: restored.current });
        setScreen('mismatch');
        return;
      }
      if (restored.kind === 'active' || restored.kind === 'unverified') {
        const active = restored.session;
        setSession(active);
        setAnswers((current) => ({ ...current, ...active.answers }));
        const sender = ensureSender(active);
        if (restored.kind === 'unverified') {
          // 세션 조회가 일시 실패했다. 통신 오류면 연결을 다시 점검하게 한다(REV-002).
          noteFailure(restored.code);
          setNotice(t.error.network);
        }
        if (active.submitted) {
          /*
           * 이미 확정된 세션이다. 확정 결과 문서를 다시 읽어 검증한다 —— 그것을
           * 확인하기 전에는 `저장 완료`로 표시하지 않는다(REV-006·REV-007).
           *
           * 대기열도 서버의 완료 경계로 봉인한다 (CR-03). 앞선 버전에서 뒤로가기로 되살아난
           * 게임이 남긴 꼬리가 있어도 `서버 확인 N / 기록 N`, 미전송 0으로 돌아온다. 서버가
           * 완료를 다시 확인하지 못하면 봉인하지 않고 자료를 그대로 둔다.
           */
          void sender.resync().then(
            (next) => {
              if (!cancelled) {
                setStatus(next);
              }
            },
            () => undefined,
          );
          void fetchResult(deps, (error) => {
            noteFailure(error.code);
          }).then(setResult, () => {
            setResult(undefined);
          });
          setScreen('done');
        } else if (requestedSurvey || active.q1_locked) {
          setScreen(requestedSurvey ? 'survey' : 'play');
        } else {
          setScreen('q1');
        }
        return;
      }
      if (restored.kind === 'unreachable') {
        /*
         * 이 기기의 표식 없이 cookie로 기존 참여를 확인하려 했지만 통신·서버 오류로 확인하지
         * 못했다 (REV-002). 세션이 없다고 보고 새 참여를 열면 기존 참가자가 새로 시작하게 된다.
         * 통신 오류만 연결 점검에 알리고, 확인할 수 있을 때까지 여기서 멈춘다.
         */
        noteFailure(restored.code);
        setScreen('unreachable');
        setNotice(t.campaign.unreachable);
        return;
      }
      if (restored.kind === 'gone') {
        setNotice(restored.code === 'session_withdrawn' ? t.error.sessionWithdrawn : t.error.sessionExpired);
      }
      /*
       * 지난번 세션 생성의 결과를 확정하지 못했으면 같은 intent로 먼저 회수한다(CR-01 §4).
       * 새로 시작하면 늦게 commit된 참여가 남는다. 표식이 없으면 요청하지 않는다.
       */
      const resumed = await resumePending(deps, mode.campaignId);
      if (cancelled) {
        return;
      }
      // 회수의 통신 오류·미확정도 연결 점검에 알린다(REV-002). 서버의 거절은 알리지 않는다.
      if (resumed?.kind === 'rejected') {
        noteFailure(resumed.code);
      } else if (resumed?.kind === 'pending') {
        link?.reportFailure();
      }
      if (resumed !== undefined && resumed.kind !== 'rejected') {
        applyStart(resumed);
        return;
      }
      if (resumed?.kind === 'rejected') {
        setNotice(startRejection(resumed.code));
      }
      setScreen(outcome.kind === 'closed' ? 'closed' : 'consent');
    })();
    return () => {
      cancelled = true;
    };
  }, [deps, ensureSender, mode, requestedSurvey, loadRound, noteFailure, link]);

  // ── 서버 연결 점검: 수동 버튼과 예약 일정이 같은 점검을 쓴다(CR-01 §2) ─────────
  useEffect(() => {
    if (link === undefined) {
      return;
    }
    setConnection(link.state());
    const stop = link.start();
    const unsubscribe = link.subscribe(setConnection);
    return () => {
      unsubscribe();
      stop();
    };
  }, [link]);

  /*
   * 캠페인 안내나 기존 참여를 확인하지 못했던 화면은 **연결 안 됨에서 연결됨으로 바뀔 때**
   * 첫 진입을 다시 한다. 점검은 되는데 특정 요청만 계속 실패하는 경우에 요청 실패 보고 →
   * 재점검 → 연결됨이 되풀이되며 몇 초마다 다시 읽지 않게 한다.
   */
  const wasDown = useRef(false);
  useEffect(() => {
    if (connection === 'disconnected') {
      wasDown.current = true;
      return;
    }
    if (connection === 'connected' && wasDown.current && screen === 'unreachable') {
      wasDown.current = false;
      setLoadRound((round) => round + 1);
    }
  }, [connection, screen]);

  // ── 전송 주기: 10초 / 유효 이동 50회 / 화면을 떠날 때 ────────────────────
  /*
   * 같은 참여 동안에는 주기를 다시 걸지 않는다 (TASK-2026-012 CR-01). 예전에는 답변을 저장할
   * 때마다 `session` 객체가 바뀌어 주기가 다시 걸렸고, 정리 단계의 `release()`가 제출 도중에
   * 쓰기 임차를 놓아 제출의 전송이 `not_holder`로 끝났다.
   */
  const activeRef = session?.session_ref;
  /** 이 세션이 제출을 마쳤는가. 전송 주기를 다시 걸지 않고 유휴 점검 판단에만 쓴다 (CR-02). */
  const submittedRef = useRef(false);
  submittedRef.current = session?.submitted === true;
  useEffect(() => {
    const sender = senderRef.current;
    if (sender === undefined || activeRef === undefined) {
      return;
    }
    // 제출을 마친 세션은 더 이상 활성 참가가 아니므로 유휴 /health 점검을 멈춘다 (CR-02).
    return startSenderLoop(sender, { onStatus: setStatus, idleActive: () => !submittedRef.current });
  }, [activeRef]);

  function setAnswer(id: string, value: AnswerValue): void {
    setAnswers((current) => ({ ...current, [id]: value }));
    setIssues((current) => current.filter((issue) => issue.id !== id));
  }

  /**
   * 세션 시작 결과를 화면에 반영한다 (TASK-2026-011).
   *
   * - `pending`은 거부가 아니다. 결과 확인 화면에서 같은 intent로 다시 확인한다.
   * - 회수한 세션이 다른 링크의 캠페인이면 이 링크의 자료와 섞지 않고 멈춘다.
   * - 같은 코호트 참여는 최초 캠페인으로 안내한다.
   */
  function applyStart(outcome: StartOutcome): void {
    if (outcome.kind === 'started') {
      // 코드는 화면에도 남기지 않는다. 세션은 cookie로만 이어진다.
      setInviteCode('');
      setNotice(undefined);
      setSession(outcome.session);
      ensureSender(outcome.session);
      setScreen('q1');
      return;
    }
    if (outcome.kind === 'pending') {
      setNotice(undefined);
      setScreen('pending');
      return;
    }
    if (outcome.kind === 'joined') {
      setInviteCode('');
      setMismatch({ reason: 'cohort', current: outcome.campaignId });
      setScreen('mismatch');
      return;
    }
    if (outcome.kind === 'other_campaign') {
      // flow가 이 링크의 참여로 저장하지 않았다(REV-001). 원래 캠페인으로 안내한다.
      setInviteCode('');
      setNotice(undefined);
      setMismatch({ reason: 'campaign', current: outcome.campaignId });
      setScreen('mismatch');
      return;
    }
    setNotice(startRejection(outcome.code));
    if (screen === 'pending' || outcome.code === 'session_intent_expired' || outcome.code === 'session_intent_required') {
      setScreen('consent');
    }
  }

  /** 동의 뒤 세션을 시작한다. 공개 캠페인은 초대코드 화면을 거치지 않는다(SPEC §6). */
  async function begin(code: string | undefined): Promise<void> {
    if (!isOnline(mode) || config === undefined) {
      return;
    }
    setBusy(true);
    const outcome = await startSession(deps, {
      campaignId: mode.campaignId,
      consentVersion: config.consent_version,
      ...(code === undefined ? {} : { inviteCode: code }),
    });
    setBusy(false);
    noteStartFailure(outcome);
    applyStart(outcome);
  }

  /**
   * 세션 시작·회수의 실패를 연결 점검에 알린다 (REV-002). 거부는 코드가 통신 오류일 때만,
   * `pending`은 통신·서버 오류로 결과를 확정하지 못한 경우라 점검으로 도달성을 다시 본다.
   */
  function noteStartFailure(outcome: StartOutcome): void {
    if (outcome.kind === 'rejected') {
      noteFailure(outcome.code);
    } else if (outcome.kind === 'pending') {
      link?.reportFailure();
    }
  }

  async function onStart(): Promise<void> {
    if (!consented) {
      setNotice(t.consent.required);
      return;
    }
    setNotice(undefined);
    if (config?.entry_mode === 'open') {
      await begin(undefined);
      return;
    }
    setScreen('invite');
  }

  async function onInvite(): Promise<void> {
    await begin(inviteCode);
  }

  /** 결과 확인 화면의 `다시 확인`. 새 세션을 만들지 않고 같은 intent의 결과만 회수한다. */
  async function onRecheck(): Promise<void> {
    if (!isOnline(mode) || config === undefined) {
      return;
    }
    setBusy(true);
    const outcome = await recoverPending(deps, { campaignId: mode.campaignId, consentVersion: config.consent_version });
    setBusy(false);
    noteStartFailure(outcome);
    applyStart(outcome);
  }

  async function onSaveQ1(): Promise<void> {
    if (session === undefined) {
      return;
    }
    setBusy(true);
    const outcome = await saveAnswers(deps, session, answers, { final: false, lockQ1: true });
    setBusy(false);
    if (outcome.kind === 'issues') {
      setIssues(outcome.issues);
      return;
    }
    if (outcome.kind === 'failed') {
      noteFailure(outcome.code);
      setNotice(outcome.retriable ? t.error.network : t.error.unexpected);
      return;
    }
    setSession(outcome.session);
    setNotice(undefined);
    setScreen('play');
  }

  async function onSubmit(): Promise<void> {
    if (session === undefined) {
      return;
    }
    setBusy(true);
    setBlocking([]);
    /*
     * 최종 답변은 한 번만 저장된다. 서버는 저장된 최종 답변을 바꾸지 않는다
     * (`response_final_locked`). 앞선 제출이 기록 전송을 기다리다 멈췄다면 저장을 건너뛰고
     * 전송·완료만 다시 한다 (TASK-2026-012 CR-01 AC04).
     */
    let active = session;
    if (!finalSaved) {
      const saved = await saveAnswers(deps, session, answers, { final: true });
      if (saved.kind === 'issues') {
        setIssues(saved.issues);
        setBusy(false);
        return;
      }
      if (saved.kind === 'failed' && saved.code !== 'response_final_locked') {
        noteFailure(saved.code);
        setNotice(saved.retriable ? t.error.network : t.error.unexpected);
        setBusy(false);
        return;
      }
      if (saved.kind === 'saved') {
        active = saved.session;
      } else {
        /*
         * 서버가 이미 잠근 최종 응답이다 (REV-003). 새로고침으로 이 화면이 몰랐을 수도, 다른
         * 탭이 다른 답을 저장했을 수도 있다. 이 기기 저장소는 증거가 되지 못하므로 **서버로**
         * 확인한다: 이 참여이고 서버가 잠근 본문이 이 화면의 답과 같을 때만 이어 간다.
         * 증명하지 못하면 `/complete` 없이 멈추고 참가자에게 알린다.
         */
        const locked = await confirmLockedFinal(deps, session, answers);
        if (locked.kind === 'failed') {
          noteFailure(locked.code);
          setNotice(locked.code === 'network_error' ? t.error.network : t.error.unexpected);
          setBusy(false);
          return;
        }
        if (locked.kind !== 'same') {
          setFinalConflict(true);
          setNotice(t.submitHeld.conflict(locked.kind === 'mismatch' ? 'session_mismatch' : 'answers_differ'));
          setBusy(false);
          return;
        }
        active = locked.session;
      }
      setSession(active);
      setFinalSaved(true);
    }

    /*
     * 게임에서 정상적으로 나오지 못해 끝이 없는 시도가 남아 있으면 서버가 완료를
     * 확정하지 않는다. 설문 화면에서 그 시도를 `interrupted`로 닫아, 끝까지 하지 않은
     * 참가자의 의견도 확정될 수 있게 한다(AC-02).
     */
    // 이 세션의 키 공간만 본다. 연습 탭의 열린 시도는 이 세션의 시도가 아니다(R2-REV-001).
    const log = telemetryFor(active.session_ref);
    const store = checkpointsFor(active.session_ref);
    closeDanglingAttempt({
      read: () => store.read(),
      save: (state) => store.save(state),
      readAll: () => log.readAll(),
      record: (name, payload) => log.record(name, payload),
      now: () => Date.now(),
    });

    const sender = ensureSender(active);
    /*
     * 완료 방어선 (TASK-2026-012 CR-01 §5~§7). 최종 답변을 저장한 뒤 실제로 보내고, 이 세션의
     * 기록이 **모두** ACK된 것을 최신 상태로 확인한 뒤에만 `/complete`를 한 번 보낸다.
     * 예전에는 동기화되지 않아도 그때의 서버 확인 순번을 `last_seq`로 보내, 남은 기록이
     * 빠진 채 완료가 확정될 수 있었다.
     */
    /*
     * 방금 `setSession`이 전송 주기를 다시 걸면서 앞선 주기가 임차를 놓았다. 제출은 참가자가
     * 직접 누른 동작이므로 `지금 다시 보내기`처럼 다시 쓸 수 있게 한다. 다른 탭이 쥔 임차는
     * `acquire()`가 여전히 막는다.
     */
    sender.resume();
    const finalized = await finalizeSubmission({
      sender,
      complete: (lastSeq) =>
        submit(deps, active, { ending, lastSeq }, (error) => {
          noteFailure(error.code);
        }),
      wait: (ms) =>
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, ms);
        }),
      now: () => Date.now(),
    });
    setStatus(sender.status());
    setBusy(false);
    if (finalized.kind === 'held') {
      // 의견은 저장됐다. 기록은 대기열에 남아 있고 `/complete`는 보내지 않았다.
      noteFailure(finalized.lastError ?? '');
      setNotice(
        finalized.reason === 'unsynced'
          ? t.submitHeld.unsynced
          : finalized.reason === 'readonly'
            ? t.submitHeld.readonly
            : t.submitHeld.blocked(finalized.lastError ?? ''),
      );
      return;
    }
    const outcome = finalized.result;
    if (outcome.kind === 'complete') {
      /*
       * 서버가 완료를 확정했다. 이 기기의 대기열을 완료 경계로 봉인한다 (TASK-2026-012 CR-03).
       * 봉인은 세션 조회로 서버가 완료를 다시 확인할 때만 일어나며, 그 뒤 이 기기에만 생긴
       * 기록은 보내지도 미전송으로 세지도 않는다.
       */
      void sender.resync().then(setStatus, () => undefined);
      setSession(outcome.session);
      setDigest(outcome.digest);
      setResult(outcome.result);
      /*
       * 확정 결과를 읽지 못했으면 그 사실을 말한다 (REV-006·REV-007). 서버가 완료를
       * 확정한 것과 이 화면이 확정본을 확인한 것은 다르다.
       */
      setNotice(outcome.result === undefined ? t.result.unverified : undefined);
      setScreen('done');
      return;
    }
    if (outcome.kind === 'incomplete') {
      setBlocking(outcome.blocking);
      setNotice(t.status.incomplete);
      return;
    }
    // `/complete` 요청 자체의 통신 오류는 연결 점검에 알린다(REV-002).
    noteFailure(outcome.code);
    setNotice(outcome.retriable ? t.error.network : t.error.unexpected);
  }

  async function onRetry(): Promise<void> {
    const sender = senderRef.current;
    if (sender === undefined) {
      return;
    }
    setBusy(true);
    // 숨었다가 돌아온 탭은 임차를 놓은 상태다. 다시 쓸 수 있게 한 뒤 보낸다(REV-002).
    sender.resume();
    await sender.resync();
    /*
     * 참가자가 직접 누른 재시도는 브라우저의 온라인 표시를 넘어 실제로 보낸다 (TASK-2026-012).
     * 연결이 돌아온 뒤에도 그 표시가 거짓으로 남아 재시도가 요청조차 만들지 못한 사례가
     * 있다. 결과는 서버 응답으로만 판정하고, 실패면 자료가 그대로 남는다.
     */
    await sender.flushAll({ manual: true });
    setStatus(sender.status());
    setBusy(false);
  }

  /** 확정 결과를 다시 읽어 검증한다 (REV-007). */
  async function onRecheckResult(): Promise<void> {
    setBusy(true);
    const verified = await fetchResult(deps, (error) => {
      noteFailure(error.code);
    });
    setResult(verified);
    setNotice(verified === undefined ? t.result.unverified : undefined);
    setBusy(false);
  }

  /**
   * 확정 결과를 **받은 바이트 그대로** 내려받는다 (REV-007).
   *
   * 다시 조립하지 않는다. 화면에 보이는 문자열, 내려받는 파일, `GET /result`의 응답,
   * envelope이 스스로 적은 digest가 모두 같은 한 문서를 가리켜야 한다.
   */
  function onDownloadResult(): void {
    if (result === undefined) {
      return;
    }
    const blob = new Blob([result.text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = resultFileName(result.body.session_ref);
    anchor.click();
    URL.revokeObjectURL(url);
  }

  /** 저장된 다른 캠페인의 참여를 버리고 지금 링크의 캠페인으로 새로 시작한다 (REV-008). */
  function onFreshStart(): void {
    clearSessionState(window.localStorage);
    setRecordingSession(undefined);
    setMismatch(undefined);
    setSession(undefined);
    setNotice(undefined);
    setScreen('consent');
  }

  /**
   * 복구 파일을 권할 상태인가. 미전송이 남았거나 확인 기록을 잃었거나 확인할 수 없다. 잠긴 최종
   * 응답이 이 화면의 답과 같다고 확인되지 않아 제출을 멈췄을 때도 권한다(REV-003).
   */
  const needsRecovery =
    finalConflict || (status !== undefined && (!status.synced || status.confirmationsState !== 'complete'));

  async function onWithdraw(): Promise<void> {
    if (session === undefined) {
      return;
    }
    setBusy(true);
    const outcome = await withdraw(deps, session);
    setBusy(false);
    if (outcome.kind === 'withdrawn') {
      senderRef.current?.release();
      senderRef.current = undefined;
      setSession(undefined);
      setNotice(t.status.withdrawn);
      setScreen('declined');
    } else {
      noteFailure(outcome.code);
      setNotice(t.error.network);
    }
  }

  function onDownloadRecovery(): void {
    // 복구 파일은 이 참여의 기록이다. 세션이 없으면 이 페이지가 쓰던 연습 기록을 싣는다.
    const log = telemetryFor(session?.session_ref);
    const payload = {
      schema: 'ccatlas.collect-recovery/1',
      exported_at: Date.now(),
      session_ref: session?.session_ref ?? null,
      campaign_id: session?.campaign_id ?? null,
      queue: session === undefined ? null : readQueueState(window.localStorage, session.session_ref),
      /*
       * 이 세션의 저장 확인 기록 spool 상태 (CR-01 A+ §2.5). 확인 불가 사유·미전송·누적 손실을
       * 담당자에게 전달하는 이 파일에 남긴다. 확인 기록 원문 전체는 싣지 않는다.
       */
      confirmation_spool:
        status === undefined
          ? null
          : {
              state: status.confirmationsState,
              reason: status.confirmationsReason,
              pending: status.pendingConfirmations,
              dropped: status.droppedConfirmations,
            },
      telemetry: JSON.parse(log.toJson()) as unknown,
    };
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = recoveryFileName();
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const surveyQuestions = questionsOf('survey');
  const missingCount = unansweredIds(answers, 'survey').length;
  /*
   * 서버에 닿는다고 확인되지 않았으면 서버가 필요한 동작을 막는다 (CR-01 §3). 아직 서버에
   * 저장하지 않은 입력도 막아, 새로고침으로 사라질 입력을 받은 것처럼 보이지 않게 한다.
   * 이미 입력한 값은 지우지 않는다. `지금 다시 보내기`·복구 파일·게임 이동은 그대로다.
   *
   * **실제 점검이 연결을 확인했을 때만** 연다 (REV-001). 점검 전(`unknown`)은 브라우저가
   * 온라인이라고 해도 확인이 아니다. 화면을 열면 점검이 곧바로 한 번 돈다.
   */
  const offline = link !== undefined && !link.confirmed();
  const FREEZE: React.CSSProperties = { border: 0, padding: 0, margin: 0, minWidth: 0 };

  return (
    <main
      style={{
        boxSizing: 'border-box',
        maxWidth: '40rem',
        margin: '0 auto',
        padding: '1rem',
        fontFamily: 'system-ui, sans-serif',
        lineHeight: 1.6,
      }}
    >
      <h1 style={{ fontSize: '1.25rem', margin: '0 0 1rem' }}>{t.title}</h1>

      {notice !== undefined && (
        <p role="status" data-testid="notice" style={{ ...CARD, background: '#FFF8E1', borderColor: '#E0C060' }}>
          {notice}
        </p>
      )}

      {link !== undefined && (connection !== 'unknown' || offline) && (
        <section style={CARD} data-testid="connection-panel">
          <p data-testid="connection-status" data-state={connection} style={{ margin: 0, fontWeight: 600 }}>
            {t.connection[connection]}
          </p>
          {offline && (
            <>
              <p style={{ margin: '0.25rem 0 0.5rem', color: '#4A4A4A', fontSize: '0.9rem' }}>{t.connection.limited}</p>
              <button
                type="button"
                style={BUTTON}
                data-testid="connection-check"
                disabled={connection === 'checking'}
                onClick={() => void link.requestCheck()}
              >
                {t.connection.check}
              </button>
            </>
          )}
        </section>
      )}

      {screen === 'loading' && <p data-testid="screen-loading">{t.loading}</p>}

      {screen === 'local' && (
        <section style={CARD} data-testid="screen-local">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.play.heading}</h2>
          {/* 캠페인 없이 열린 화면이다. 원격 계측 없이 연습만 할 수 있다. */}
          <p>{t.consent.beforeConsent}</p>
          <a href={playUrl('')} style={{ ...BUTTON, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#1A1A1A' }}>
            {t.play.start}
          </a>
        </section>
      )}

      {screen === 'missing' && <p data-testid="screen-missing" style={CARD}>{t.campaign.notFound}</p>}
      {screen === 'unreachable' && <p data-testid="screen-unreachable" style={CARD}>{t.campaign.unreachable}</p>}

      {screen === 'closed' && (
        <section style={CARD} data-testid="screen-closed">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.campaign.closedTitle}</h2>
          <p>{t.campaign.closed}</p>
        </section>
      )}

      {screen === 'declined' && <p data-testid="screen-declined" style={CARD}>{t.consent.declined}</p>}

      {screen === 'mismatch' && mismatch !== undefined && (
        <section style={CARD} data-testid="screen-mismatch" data-reason={mismatch.reason}>
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>
            {mismatch.reason === 'campaign'
              ? t.mismatch.campaignTitle
              : mismatch.reason === 'session'
                ? t.mismatch.sessionTitle
                : mismatch.reason === 'cohort'
                  ? t.mismatch.cohortTitle
                  : t.mismatch.consentTitle}
          </h2>
          <p>
            {mismatch.reason === 'campaign'
              ? t.mismatch.campaign
              : mismatch.reason === 'session'
                ? t.mismatch.session
                : mismatch.reason === 'cohort'
                  ? t.mismatch.cohort
                  : t.mismatch.consent}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(mismatch.reason === 'campaign' || mismatch.reason === 'cohort') && (
              <a
                href={collectUrl({ campaignId: mismatch.current })}
                data-testid="mismatch-resume"
                style={{ ...BUTTON, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#1A1A1A' }}
              >
                {t.mismatch.resume}
              </a>
            )}
            {/* 같은 코호트에는 새로 시작해도 서버가 막는다. 새로 시작 버튼을 보이지 않는다. */}
            {mismatch.reason !== 'cohort' && (
              <button type="button" style={PRIMARY} data-testid="mismatch-fresh" onClick={onFreshStart}>
                {mismatch.reason === 'consent_version' ? t.mismatch.reconsent : t.mismatch.fresh}
              </button>
            )}
          </div>
        </section>
      )}

      {screen === 'pending' && (
        <section style={CARD} data-testid="screen-pending">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.start.pendingTitle}</h2>
          <p>{t.start.pending}</p>
          <button type="button" style={primaryButton(busy || offline)} data-testid="pending-recheck" disabled={busy || offline} onClick={() => void onRecheck()}>
            {t.start.recheck}
          </button>
        </section>
      )}

      {screen === 'consent' && config !== undefined && (
        <section style={CARD} data-testid="screen-consent">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.consent.heading}</h2>
          <p style={{ whiteSpace: 'pre-wrap' }}>{config.consent_text}</p>
          <p style={{ color: '#4A4A4A' }}>{t.consent.beforeConsent}</p>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minHeight: '44px' }}>
            <input
              type="checkbox"
              data-testid="consent-check"
              checked={consented}
              onChange={(event) => {
                setConsented(event.target.checked);
              }}
            />
            {t.consent.agree}
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <button type="button" style={primaryButton(busy || offline)} data-testid="consent-start" disabled={busy || offline} onClick={() => void onStart()}>
              {t.consent.start}
            </button>
            <button
              type="button"
              style={BUTTON}
              data-testid="consent-decline"
              onClick={() => {
                setScreen('declined');
              }}
            >
              {t.consent.decline}
            </button>
          </div>
        </section>
      )}

      {screen === 'invite' && (
        <section style={CARD} data-testid="screen-invite">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.invite.heading}</h2>
          <p>{t.invite.help}</p>
          <label style={{ display: 'grid', gap: '0.25rem' }}>
            {t.invite.label}
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              data-testid="invite-input"
              value={inviteCode}
              onChange={(event) => {
                setInviteCode(event.target.value);
              }}
              style={{ fontSize: '1rem', padding: '0.5rem', minHeight: '44px', boxSizing: 'border-box', width: '100%' }}
            />
          </label>
          <button
            type="button"
            style={primaryButton(busy || offline || inviteCode.trim() === '', { marginTop: '0.5rem' })}
            data-testid="invite-submit"
            disabled={busy || offline || inviteCode.trim() === ''}
            onClick={() => void onInvite()}
          >
            {t.invite.submit}
          </button>
        </section>
      )}

      {screen === 'q1' && (
        <section style={CARD} data-testid="screen-q1">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.q1.heading}</h2>
          <p style={{ margin: '0 0 0.75rem', color: '#4A4A4A', fontSize: '0.9rem' }} data-testid="q1-empty-hint">
            {t.q1.emptyHint}
          </p>
          <fieldset disabled={offline} style={FREEZE}>
            {questionsOf('q1').map((question) => (
              <Field
                key={question.id}
                question={question}
                value={answers[question.id] ?? null}
                issue={issues.find((issue) => issue.id === question.id)}
                onChange={(value) => {
                  setAnswer(question.id, value);
                }}
              />
            ))}
          </fieldset>
          <button type="button" style={primaryButton(busy || offline)} data-testid="q1-submit" disabled={busy || offline} onClick={() => void onSaveQ1()}>
            {t.q1.submit}
          </button>
        </section>
      )}

      {screen === 'play' && isOnline(mode) && (
        <section style={CARD} data-testid="screen-play">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.play.heading}</h2>
          <p>{t.play.note}</p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <a
              href={playUrl(mode.campaignId)}
              data-testid="play-link"
              style={{ ...PRIMARY, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
            >
              {t.play.start}
            </a>
            <a
              href={collectUrl({ campaignId: mode.campaignId, step: 'survey' })}
              data-testid="survey-link"
              style={{ ...BUTTON, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#1A1A1A' }}
            >
              {t.play.survey}
            </a>
          </div>
        </section>
      )}

      {screen === 'survey' && (
        <section style={CARD} data-testid="screen-survey">
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.survey.heading}</h2>
          <p style={{ color: '#4A4A4A' }}>{t.survey.note}</p>
          {session?.q1_locked === true && <p style={{ color: '#4A4A4A' }}>{t.q1.locked}</p>}
          <fieldset disabled={offline || finalSaved || finalConflict} style={FREEZE}>
            {surveyQuestions.map((question) => (
              <Field
                key={question.id}
                question={question}
                value={answers[question.id] ?? null}
                issue={issues.find((issue) => issue.id === question.id)}
                onChange={(value) => {
                  setAnswer(question.id, value);
                }}
              />
            ))}
          </fieldset>
          {missingCount > 0 && <p data-testid="unanswered">{t.survey.unanswered(missingCount)}</p>}
          {blocking.length > 0 && (
            <p role="alert" data-testid="blocking" style={{ color: '#B00020' }}>
              {t.status.incomplete} ({blocking.join(', ')})
            </p>
          )}
          <button
            type="button"
            style={primaryButton(busy || offline || finalConflict)}
            data-testid="survey-submit"
            disabled={busy || offline || finalConflict}
            onClick={() => void onSubmit()}
          >
            {t.survey.submit}
          </button>
        </section>
      )}

      {screen === 'done' && (
        <section style={CARD} data-testid="screen-done" data-verified={result === undefined ? 'false' : 'true'}>
          {/*
            제목도 판정을 따른다 (REV-006). 확정 결과를 확인하기 전에는 `저장 완료`라고
            쓰지 않는다 —— 서버가 완료를 확정한 것과 이 화면이 그것을 확인한 것은 다르다.
          */}
          <h2 style={{ fontSize: '1.05rem', marginTop: 0 }} data-testid="done-heading">
            {doneView(status, result !== undefined).heading}
          </h2>
          <p data-testid="done-note">{doneView(status, result !== undefined).note}</p>
          {result === undefined ? (
            <button type="button" style={BUTTON} data-testid="result-recheck" disabled={busy || offline} onClick={() => void onRecheckResult()}>
              {t.result.recheck}
            </button>
          ) : (
            <>
              <h3 style={{ fontSize: '0.95rem', marginBottom: '0.25rem' }}>{t.result.heading}</h3>
              <p style={{ margin: '0 0 0.5rem', color: '#4A4A4A', fontSize: '0.9rem' }}>{t.result.note}</p>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
                {t.result.digest}:{' '}
                <span
                  data-testid="result-digest"
                  style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
                >
                  {result.digest}
                </span>
              </p>
              {/* 기본은 접힌 상태다(전역 UI 표준). 펼치면 확정본 원문이 그대로 보인다. */}
              <details>
                <summary style={{ minHeight: '44px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  {t.result.show}
                </summary>
                <pre
                  data-testid="result-json"
                  style={{
                    maxHeight: '18rem',
                    overflow: 'auto',
                    background: '#F5F5F5',
                    padding: '0.5rem',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {result.text}
                </pre>
              </details>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                <button type="button" style={PRIMARY} data-testid="result-download" onClick={onDownloadResult}>
                  {t.result.download}
                </button>
                <button type="button" style={BUTTON} data-testid="result-recheck" disabled={busy || offline} onClick={() => void onRecheckResult()}>
                  {t.result.recheck}
                </button>
              </div>
            </>
          )}
          {digest !== undefined && digest !== '' && result === undefined && (
            <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{digest}</p>
          )}
        </section>
      )}

      {session !== undefined && screen !== 'declined' && (
        <section style={CARD} data-testid="status-panel" aria-live="polite">
          {/*
            복구 파일은 보낼 것이 남았을 때뿐 아니라 확인 기록을 잃었거나 확인할 수 없을 때도
            내려받게 한다 (CR-01 A+ §2.5). 완료 화면의 안내가 이 버튼을 가리킨다.
          */}
          <h2 style={{ fontSize: '1rem', marginTop: 0 }}>{t.status.heading}</h2>
          <p data-testid="status-label" style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>
            {statusLabel(status, { submitted: session.submitted, resultVerified: result !== undefined })}
          </p>
          <p style={{ margin: 0, color: '#4A4A4A', fontSize: '0.9rem' }} data-testid="status-detail">
            {t.status.detail(status?.throughSeq ?? 0, status?.localMaxSeq ?? 0)}
          </p>
          {/* 확인 기록의 미전송·유실도 숨기지 않는다(REV-005). */}
          <p style={{ margin: 0, color: '#4A4A4A', fontSize: '0.9rem' }} data-testid="status-confirmations">
            {t.status.confirmationDetail(status?.pendingConfirmations ?? 0, status?.droppedConfirmations ?? 0)}
          </p>
          {status !== undefined && !status.synced && <p style={{ margin: '0.5rem 0 0' }}>{t.status.pendingNote}</p>}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <button type="button" style={BUTTON} data-testid="status-retry" disabled={busy} onClick={() => void onRetry()}>
              {t.status.retry}
            </button>
            {needsRecovery && (
              <button type="button" style={BUTTON} data-testid="status-recovery" onClick={onDownloadRecovery}>
                {t.status.recovery}
              </button>
            )}
            <button type="button" style={BUTTON} data-testid="status-withdraw" disabled={busy || offline} onClick={() => void onWithdraw()}>
              {t.status.withdraw}
            </button>
          </div>
          {needsRecovery && (
            <p style={{ margin: '0.5rem 0 0', color: '#4A4A4A', fontSize: '0.9rem' }}>{t.status.recoveryNote}</p>
          )}
        </section>
      )}
    </main>
  );
}
